import { TableViewType } from "@/types/enums/TableViewType";
import { apiDelete, apiGet, apiPost, apiPut } from "../_apiShim";
import { TableFilter } from "@/types/TableFilter";

const BASE_PATH = "/api/v1/table-filters/";
const FILTERS_FIELDS = "tableFilterId,name,filter,conditions,description,updatedAt";

// Create a table filter for events
export async function createTableFilterEvents(
  tableFilter: TableFilter
): Promise<string> {
  const response = await apiPost<string>(
    `${BASE_PATH}create-table-filter-events`,
    {
      name: tableFilter.name,
      description: tableFilter.description,
      filter: tableFilter.filter,
    },
    {
      "Cache-Control": "no-store",
    }
  );

  if (!response) throw new Error("unknown");

  return response;
}

// Get table filters for events
export async function getTableFiltersEvents(
  excludedIds: string[],
  fields: string,
  page: number = 0,
  size: number = 10
): Promise<TableFilter[]> {
  const excludedIdsEncoded = excludedIds.map(encodeURIComponent).join(",");

  const response = await apiGet<TableFilter[]>(
    `${BASE_PATH}get-table-filter-events?fields=${encodeURIComponent(
      fields
    )}&page=${encodeURIComponent(page)}&size=${encodeURIComponent(
      size
    )}&excludedIds=${excludedIdsEncoded}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) return [];

  return response;
}

// Update a table filter for events
export async function updateTableFilterEvents(
  tableFilter: TableFilter
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${tableFilter.tableFilterId}`,
    {
      name: tableFilter.name,
      description: tableFilter.description,
      filter: tableFilter.filter,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete a table filter for events
export async function deleteTableFilterEvents(
  tableFilterId: string
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${tableFilterId}`, {
    "Cache-Control": "no-store",
  });
}

// Create a table filter for guests
export async function createTableFilterGuests(
  eventId: string,
  tableFilter: TableFilter
): Promise<string> {
  const response = await apiPost<string>(
    `${BASE_PATH}${eventId}/create-table-filter-guests`,
    {
      type: tableFilter.tableViewType,
      name: tableFilter.name,
      description: tableFilter.description,
      filter: tableFilter.filter,
      // Dual-write: the nested tree is the canonical shape post-migration.
      ...(tableFilter.conditions && { conditions: tableFilter.conditions }),
    },
    {
      "Cache-Control": "no-store",
    }
  );

  if (!response) throw new Error("unknown");

  return response;
}

// Get table filters for guests
export async function getTableFiltersGuests(
  eventId: string,
  excludedIds: string[],
  fields: string,
  page: number = 0,
  size: number = 10,
  type: TableViewType = TableViewType.GUEST
): Promise<TableFilter[]> {
  const excludedIdsEncoded = excludedIds.map(encodeURIComponent).join(",");

  const response = await apiGet<TableFilter[]>(
    `${BASE_PATH}${eventId}/get-table-filter-guests?fields=${encodeURIComponent(
      fields
    )}&page=${encodeURIComponent(page)}&size=${encodeURIComponent(
      size
    )}&excludedIds=${excludedIdsEncoded}&type=${type.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) return [];

  return response;
}

// Update a table filter for guests
export async function updateTableFilterGuests(
  eventId: string,
  tableFilter: TableFilter
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/${tableFilter.tableFilterId}`,
    {
      name: tableFilter.name,
      description: tableFilter.description,
      filter: tableFilter.filter,
      ...(tableFilter.conditions && { conditions: tableFilter.conditions }),
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete a table filter for guests
export async function deleteTableFilterGuests(
  eventId: string,
  tableFilterId: string
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/${tableFilterId}`, {
    "Cache-Control": "no-store",
  });
}

// Get table filter by ID for events
export async function getTableFilterEventsById(
  tableFilterId: string
): Promise<TableFilter> {
  const response = await apiGet<TableFilter>(
    `${BASE_PATH}filter/${tableFilterId}?fields=${encodeURIComponent(
      FILTERS_FIELDS
    )}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) throw new Error("unknown");

  return response;
}

// Get table filter by ID for guests
export async function getTableFilterGuestsById(
  tableFilterId: string,
  eventId: string,
  type: TableViewType = TableViewType.GUEST
): Promise<TableFilter> {
  const response = await apiGet<TableFilter>(
    `${BASE_PATH}filter/${eventId}/${tableFilterId}?fields=${encodeURIComponent(
      FILTERS_FIELDS
    )}&type=${type.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) throw new Error("unknown");

  return response;
}

// Search table filters for events
export async function searchTableFiltersEvents(
  searchTerm: string,
  fields: string,
  page: number = 0,
  size: number = 10
): Promise<TableFilter[]> {
  const response = await apiGet<TableFilter[]>(
    `${BASE_PATH}search-events?searchTerm=${encodeURIComponent(
      searchTerm
    )}&fields=${encodeURIComponent(fields)}&page=${encodeURIComponent(
      page
    )}&size=${encodeURIComponent(size)}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) return [];

  return response;
}

// Search table filters for guests
export async function searchTableFiltersGuests(
  eventId: string,
  searchTerm: string,
  fields: string,
  page: number = 0,
  size: number = 10,
  type: TableViewType = TableViewType.GUEST
): Promise<TableFilter[]> {
  const response = await apiGet<TableFilter[]>(
    `${BASE_PATH}${eventId}/search-guests?searchTerm=${encodeURIComponent(
      searchTerm
    )}&fields=${encodeURIComponent(fields)}&page=${encodeURIComponent(
      page
    )}&size=${encodeURIComponent(size)}&type=${type.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) return [];

  return response;
}
